import { selectImgsFromRoom } from "@/modes/mode-selectors";
import { selectIElementsSlice } from "@/store/ielements-slice-selector";
import {
  isLightweightAttachmentsGroup,
  isLightweightImg2d,
  isLightweightImg360,
  isLightweightRoomSection,
  isLightweightTimeSeriesRoom,
  isLightweightWithTypeAndHint,
} from "@/store/lightweight-elements-discriminators";
import {
  selectLightweightChild,
  selectLightweightDirectChildren,
  selectLightweightElement,
  selectLightweightElementsSlice,
} from "@/store/lightweight-elements-selectors";
import {
  LightweightElement,
  LightweightElementsState,
} from "@/store/lightweight-elements-slice";
import { curryAppSelector } from "@/store/reselect";
import { RootState } from "@/store/store";
import { isLocationMarker } from "@/types/ielement-extra-types";
import {
  GUID,
  IElementType,
  IElementTypeHint,
  isIElementImg360,
  isIElementRoomSection,
} from "@faro-lotv/ielement-types";
import {
  IElementsState,
  newestToOldest,
  selectChildDepthFirst,
  selectIElement,
} from "@faro-lotv/project-source";
import { createSelector } from "@reduxjs/toolkit";
import { LocationTypes } from "./element-icon-type";

/**
 * Given a room, this selector computes its location type, whether it contains 2D photos, 360 photos, both or is a location marker.
 *
 * @returns The location type of the room with the given id
 * @param state The state of the app store
 * @param id The GUID of the room element
 */
export const selectLocationType = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      selectLightweightElementsSlice,
      (state: RootState, id: GUID) => id,
    ],
    computeLocationType,
  ),
);

/**
 * Computes the location type of a given room, leveraging the iElements slice if the room
 * is loaded there, or the lightweight elements slice otherwise.
 *
 * @param iElements The iElements store slice
 * @param lightweightElements The lightweight elements store slice
 * @param id The GUID of the room element
 * @returns The location type of the room with the given id
 */
function computeLocationType(
  iElements: IElementsState,
  lightweightElements: LightweightElementsState,
  id: GUID,
): LocationTypes | undefined {
  const iElementsStore = { iElements };
  const iElement = selectIElement(id)(iElementsStore);

  if (iElement === undefined) {
    return computeLocationTypeLightweight(lightweightElements, id);
  }

  if (!isIElementRoomSection(iElement)) {
    return;
  }

  if (isLocationMarker(iElement, iElementsStore)) {
    return LocationTypes.LocationMarker;
  }

  const parentElement = selectIElement(iElement.parentId)(iElementsStore);
  if (parentElement?.type === IElementType.timeSeries) {
    // The first element of a timeseries is a Section-Room, but it's not a location
    // so here we don't return any specific type
    return;
  }

  const contain2DPhotos =
    selectImgsFromRoom(iElement)(iElementsStore).length > 0;
  const pano = selectChildDepthFirst(
    iElement,
    isIElementImg360,
  )(iElementsStore);

  if (contain2DPhotos && !!pano) {
    return LocationTypes.Photo360And2D;
  }

  if (contain2DPhotos) {
    return LocationTypes.Photo2D;
  }

  return LocationTypes.Photo360;
}

/**
 * Computes the location type of a given room, leveraging the lightweight elements slice.
 * This function is very similar to computeLocationTypes.
 *
 * @param lightweightElements The lightweight elements store slice
 * @param id The GUID of the room element
 * @returns The location type of the room with the given id
 */
function computeLocationTypeLightweight(
  lightweightElements: LightweightElementsState,
  id: GUID,
): LocationTypes | undefined {
  const lightweightElement = selectLightweightElement(id)({
    lightweightElements,
  });

  if (
    lightweightElement === undefined ||
    !isLightweightRoomSection(lightweightElement)
  ) {
    return;
  }

  if (isLightweightLocationMarker(lightweightElement, lightweightElements)) {
    return LocationTypes.LocationMarker;
  }

  if (lightweightElement.parentId) {
    const parentElement = selectLightweightElement(lightweightElement.parentId)(
      { lightweightElements },
    );
    if (parentElement?.type === IElementType.timeSeries) {
      // The first element of a timeseries is a Section-Room, but it's not a location
      // so here we don't return any specific type
      return;
    }
  }

  const contain2DPhotos =
    selectLightweightImgsFromRoom(lightweightElement)(lightweightElements)
      .length > 0;
  const pano = selectLightweightChild(
    lightweightElement,
    isLightweightImg360,
  )({ lightweightElements });

  if (contain2DPhotos && !!pano) {
    return LocationTypes.Photo360And2D;
  }

  if (contain2DPhotos) {
    return LocationTypes.Photo2D;
  }

  return LocationTypes.Photo360;
}

/**
 * This selector is very similar to 'selectImgsFromRoom', but works on the lightweight elements slice.
 *
 * @returns Select attached 2D photos from a room section
 * @param room the given room section
 */
function selectLightweightImgsFromRoom(room: LightweightElement) {
  return (lightweightElements: LightweightElementsState) => {
    const photoContainers = selectLightweightDirectChildren(room, (el) =>
      isLightweightWithTypeAndHint(
        el,
        IElementType.group,
        IElementTypeHint.attachments,
      ),
    )({ lightweightElements });

    if (photoContainers.length === 0) return [];

    return selectLightweightDirectChildren(
      photoContainers[0],
      isLightweightImg2d,
    )({ lightweightElements }).sort(newestToOldest);
  };
}

/**
 * Check if given lightweight element is a location marker holder
 *
 * @param element the given element to check
 * @param lightweightElements current store state
 * @returns true if the given element is a room section with empty time series child element
 */
function isLightweightLocationMarker(
  element: LightweightElement,
  lightweightElements: LightweightElementsState,
): boolean {
  if (!isLightweightRoomSection(element)) return false;

  const timeSeriesRoom = selectLightweightChild(
    element,
    isLightweightTimeSeriesRoom,
  )({ lightweightElements });

  const photoGroup = selectLightweightChild(
    element,
    isLightweightAttachmentsGroup,
  )({ lightweightElements });

  // room section with empty time series room and empty photo group is a location marker
  return (
    !!timeSeriesRoom &&
    !timeSeriesRoom.childrenIds?.length &&
    !photoGroup?.childrenIds?.length
  );
}
